import { useEffect, useState } from "react";
import { Loader2, ExternalLink, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, mediaUrl } from "../lib/api";

const FIELDS = [
  { key: "hero_kicker", label: "Top tagline (small text above headline)", area: false },
  { key: "hero_line1", label: "Headline line 1", area: false },
  { key: "hero_line2", label: "Headline line 2 (gradient colour)", area: false },
  { key: "hero_line3", label: "Headline line 3", area: false },
  { key: "hero_sub", label: "Hero paragraph", area: true },
  { key: "hero_image", label: "Banner image URL", area: false },
  { key: "hero_video", label: "Banner video URL (optional; replaces image on home page)", area: false },
  { key: "cta_primary", label: "Primary button text", area: false },
  { key: "cta_secondary", label: "Secondary button text", area: false },
  { key: "marquee_words", label: "Marquee words (comma separated)", area: false },
  { key: "partner_heading", label: "Partner section heading", area: true },
  { key: "partner_sub", label: "Partner section paragraph", area: true },
];

const darkInput = "mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white outline-none focus:ring-2 focus:ring-brand-cyan placeholder:text-white/25";

export default function AdminHomeContent() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    api.get("/admin/content/home").then((r) => setForm(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!form) return <p className="text-white/40">Loading...</p>;

  const uploadMedia = async (event, key) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const isVideo = key === "hero_video";
    const payload = new FormData();
    payload.append(isVideo ? "video" : "image", file);
    setUploading(key);
    try {
      const { data } = await api.post(isVideo ? "/admin/upload-video" : "/admin/upload-image", payload);
      const url = isVideo ? data.media_url : data.image_url;
      setForm((current) => ({ ...current, [key]: url }));
      toast.success(isVideo ? "Banner video uploaded" : "Banner image uploaded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading("");
      event.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/content/home", form);
      toast.success("Home page updated — live instantly");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-homepage">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold">Home page content</h2>
          <p className="text-sm text-white/40 mt-1">Edit the hero banner, headline, marquee and partner section. Changes go live instantly.</p>
        </div>
        <a href="/" target="_blank" rel="noreferrer" data-testid="hc-preview-link"
          className="flex items-center gap-2 rounded-full bg-white/10 px-5 h-10 text-sm font-semibold hover:bg-white/20">
          Preview home page <ExternalLink className="h-4 w-4" />
        </a>
      </div>
      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-brand-surface/40 p-7 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="home-content-form">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.area || f.key === "hero_image" || f.key === "hero_video" || f.key === "marquee_words" ? "sm:col-span-2" : ""}>
              <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">{f.label}</label>
              {f.key === "hero_image" || f.key === "hero_video" ? (
                <>
                  <input data-testid={`hc-${f.key}`} type="url" value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={darkInput} />
                  <label className="mt-3 inline-flex h-10 cursor-pointer items-center gap-2 rounded-full bg-white/10 px-4 text-sm font-semibold hover:bg-white/15">
                    {uploading === f.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploading === f.key ? "Uploading..." : `Upload ${f.key === "hero_video" ? "video" : "image"}`}
                    <input data-testid={`hc-${f.key}-upload`} type="file" accept={f.key === "hero_video" ? "video/mp4,video/webm" : "image/png,image/jpeg,image/webp"}
                      onChange={(e) => uploadMedia(e, f.key)} className="hidden" />
                  </label>
                  {f.key === "hero_video" && <p className="mt-2 text-xs text-white/35">MP4 or WebM, up to 100 MB. A video replaces the image in the home page banner.</p>}
                </>
              ) : f.area ? (
                <textarea data-testid={`hc-${f.key}`} rows={3} value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={darkInput} />
              ) : (
                <input data-testid={`hc-${f.key}`} value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={darkInput} />
              )}
            </div>
          ))}
        </div>
        <div className="space-y-6">
          <div className="rounded-2xl border border-white/10 bg-brand-surface/40 p-6">
            <h3 className="font-display text-lg font-bold mb-3">Banner preview</h3>
            {form.hero_video ? (
              <video src={mediaUrl(form.hero_video)} poster={mediaUrl(form.hero_image) || undefined} controls muted playsInline data-testid="hc-video-preview"
                className="w-full h-44 object-cover rounded-xl" />
            ) : form.hero_image ? (
              <img src={mediaUrl(form.hero_image)} alt="Hero banner preview" data-testid="hc-image-preview"
                className="w-full h-44 object-cover rounded-xl" />
            ) : (
              <p className="text-white/30 text-sm">No image or video set.</p>
            )}
            <p className="text-xs text-white/30 mt-3">Upload a local image or video, or paste a media URL. Wide landscape media works best.</p>
          </div>
          <button type="submit" disabled={busy} data-testid="hc-save"
            className="w-full h-12 rounded-full brand-gradient-bg text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Publish changes
          </button>
        </div>
      </form>
    </div>
  );
}
